import React from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  adoptErrorText, adoptFaceLock, adoptMinPhotos, FaceLockUploadError, FACELOCK_MAX_PHOTOS, mergePhotoPicks,
  uploadFaceLockPhotos, type FaceLockAvatar,
} from "@/lib/facelock";
import { PhotoTray } from "./FaceLockParts";

// Add photos (admin only, free): an avatar that needs its photos (a legacy one
// from before the casting crew) gets them through
// POST /api/web/facelock/admin/avatars/:id/adopt. A legacy avatar takes 3 to
// 24; the photos become the selfies a render feeds it (and, if it predates
// Flux stills files, that one file is learned from them); its picture is then
// DRAWN with the avatar itself, never taken from a photo. Any other takes 8 to
// 24. Nothing about the avatar changes
// until it finishes; if the photos do not show one face, nothing changes and
// the card says so (faceLock.messages.adopt_*).

type Props = {
  avatar: FaceLockAvatar | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStarted: (avatar: FaceLockAvatar | null) => void;
};

export default function AdoptDialog({ avatar, open, onOpenChange, onStarted }: Props) {
  const { t } = useTranslation();
  const [files, setFiles] = React.useState<File[]>([]);
  const [pickNote, setPickNote] = React.useState("");
  const [error, setError] = React.useState("");
  const [busy, setBusy] = React.useState<string | null>(null);
  const uploaded = React.useRef(new Map<File, string>());
  const avatarId = avatar?.id || "";

  // Another avatar, another set of photos.
  React.useEffect(() => {
    setFiles([]); setPickNote(""); setError("");
    uploaded.current = new Map();
  }, [avatarId]);

  if (!avatar) return null;

  const min = adoptMinPhotos(avatar);
  const max = FACELOCK_MAX_PHOTOS;
  const count = files.length;
  const ready = count >= min && count <= max && !busy;

  const addPhotos = (list: FileList | null) => {
    const picked = Array.from(list || []);
    if (!picked.length) return;
    const { next, unsupported, dropped } = mergePhotoPicks(files, picked, max);
    setFiles(next);
    setError("");
    setPickNote(unsupported.length ? t("faceLock.create.unsupported", { name: unsupported[0].name })
      : dropped ? t("faceLock.create.maxReached", { max }) : "");
  };

  const submit = async () => {
    if (!ready) return;
    setError("");
    try {
      const photos = await uploadFaceLockPhotos(files, uploaded.current, (n, total) => setBusy(t("faceLock.create.uploading", { n, total })));
      setBusy(t("faceLock.adopt.starting"));
      const res = await adoptFaceLock(avatar.id, { photos });
      if (res.ok === false) { setError(adoptErrorText(t, res, min)); return; }
      setFiles([]); setPickNote("");
      uploaded.current = new Map();
      onStarted(res.data);
      onOpenChange(false);
    } catch (e: unknown) {
      setError(e instanceof FaceLockUploadError
        ? t("faceLock.create.uploadFailed", { name: e.fileName, reason: e.reason })
        : t("faceLock.errors.network"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent overlayClassName="z-[400]" className="z-[400] max-h-[92vh] overflow-y-auto sm:max-w-xl" data-testid="facelock-adopt-dialog">
        <DialogHeader>
          <DialogTitle>{t("faceLock.adopt.title", { name: avatar.name })}</DialogTitle>
          <DialogDescription>{t("faceLock.adopt.lead", { min, max })}</DialogDescription>
        </DialogHeader>

        <div data-testid="facelock-adopt-terms" className="grid gap-1.5 rounded-lg border border-border bg-muted/40 p-3 text-sm leading-relaxed">
          <p className="font-semibold">{t("faceLock.adopt.free")}</p>
          <p>{t("faceLock.adopt.samePerson")}</p>
          <p className="text-muted-foreground">{t("faceLock.adopt.unchanged")}</p>
        </div>

        <div className="grid gap-2">
          <PhotoTray
            files={files}
            onAdd={addPhotos}
            onRemove={(i) => { setFiles((prev) => prev.filter((_, j) => j !== i)); setPickNote(""); }}
            disabled={!!busy}
            full={count >= max}
            testIdPrefix="facelock-adopt-photos"
            uploadLabel={t("faceLock.create.upload")}
            addMoreLabel={t("faceLock.create.addMore")}
          />
          <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
            <span data-testid="facelock-adopt-count" className="font-semibold">{t("faceLock.create.count", { count, max })}</span>
            {count < min && <span data-testid="facelock-adopt-need" className="text-muted-foreground">{t("faceLock.adopt.needMore", { count: min - count })}</span>}
          </div>
          {pickNote && <div data-testid="facelock-adopt-pick-note" className="text-sm text-amber-500">{pickNote}</div>}
        </div>

        {error && <div data-testid="facelock-adopt-error" role="alert" className="text-sm text-red-500">{error}</div>}

        <div className="grid gap-1.5">
          <Button type="button" data-testid="facelock-adopt-submit" disabled={!ready} onClick={() => void submit()}>
            {t("faceLock.adopt.submit")}
          </Button>
          {busy && <div data-testid="facelock-adopt-status" className="text-sm text-muted-foreground" aria-live="polite">{busy}</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
